import React, { useState, useEffect, useCallback, useRef } from 'react';
import { publicApi } from '../../../services/api';
import { SpinRequest, SpinResponse } from '../../../types';
import toast from 'react-hot-toast';
import useCelebration from '../../../hooks/useCelebration';

// Hooks
import { useWheelData } from './hooks/useWheelData';
import { useWheelSize } from './hooks/useWheelSize';
import { useWheelAudio } from './hooks/useWheelAudio';
import { useFormValidation } from './hooks/useFormValidation';

// Components
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';
import NoPrizesState from './components/NoPrizesState';
import DrawExecutedState from './components/DrawExecutedState';
import PrerequisiteModal from './components/PrerequisiteModal';
import SpinForm from './components/SpinForm';
import WheelCanvas from './components/WheelCanvas';
import ResultScreen from './components/ResultScreen';

// Utils
import { createWheelInstance } from './utils/wheelConfig';
import { usePageTitle } from '../../../hooks/usePageTitle';

interface WheelPageProps { }

const WheelPage: React.FC<WheelPageProps> = () => {
  const celebrate = useCelebration();
  const { wheelData, isLoading, loadWheelData, slug, companySlug } = useWheelData();
  window.location.href = `https://participar.rodadepremios.com.br/w/${companySlug}/${slug}`;
  const wheelSize = useWheelSize();
  const { playTickSound, playCelebrationSound } = useWheelAudio();
  const {
    validateWhatsApp,
    validateBirthdate,
    validateCustomFields,
    normalizeWhatsApp,
    customFieldsErrors,
    setCustomFieldsErrors
  } = useFormValidation();
  
  usePageTitle(wheelData ? wheelData.wheel.name : 'Roda de Prêmios');

  const [currentStep, setCurrentStep] = useState<'prerequisite' | 'form' | 'wheel' | 'result'>('prerequisite');
  const [prerequisiteConfirmed, setPrerequisiteConfirmed] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [spinResult, setSpinResult] = useState<SpinResponse | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wheelRef = useRef<any>(null);
  const previousWheelSizeRef = useRef<number>(0);

  const [formData, setFormData] = useState<SpinRequest>({
    name: '',
    whatsapp: '',
    birthdate: '',
    customData: {}
  });
  const [customFieldsData, setCustomFieldsData] = useState<Record<string, any>>({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const defaultTheme = {
    background_color: '#2563eb',
    text_color: '#ffffff',
    font_family: 'Inter',
    logo_url: undefined
  };

  const theme = wheelData?.theme || defaultTheme;

  // Carregar dados da roda ao montar
  useEffect(() => {
    if (slug) {
      loadWheelData();
    }
  }, [slug, loadWheelData]);

  // Determinar step inicial baseado em pré-requisito
  useEffect(() => {
    if (wheelData) {
      if (wheelData.wheel.prerequisite_content && !prerequisiteConfirmed) {
        setCurrentStep('prerequisite');
      } else {
        setCurrentStep('form');
      }
    }
  }, [wheelData, prerequisiteConfirmed]);

  // Celebração quando ganhar prêmio
  useEffect(() => {
    if (currentStep === 'result' && spinResult && spinResult.prize && !spinResult.prize.is_no_prize) {
      celebrate.fire();
    }
  }, [spinResult, currentStep, celebrate]);

  const createWheel = useCallback((stopAngle: number) => {
    if (!wheelData || !canvasRef.current) return;

    wheelRef.current = createWheelInstance({
      wheelData,
      wheelSize,
      stopAngle,
      onFinished: () => {
        setIsSpinning(false);
        setTimeout(() => {
          playCelebrationSound();
          setCurrentStep('result');
        }, 500);
      },
      onSound: playTickSound
    });
  }, [wheelData, wheelSize, playTickSound, playCelebrationSound]);

  // Recriar a roda quando o tamanho mudar (resize da janela)
  // APENAS se o wheelSize realmente mudou
  useEffect(() => {
    if (currentStep === 'wheel' && wheelData && spinResult && !isSpinning) {
      // Verificar se o wheelSize realmente mudou
      if (previousWheelSizeRef.current !== 0 && previousWheelSizeRef.current !== wheelSize) {
        if (spinResult.prize) {
          const prizeIndex = wheelData.products.findIndex(p => p.id === spinResult.prize!.id);
          const segmentAngle = 360 / wheelData.products.length;
          const stopAngle = (prizeIndex * segmentAngle) + (segmentAngle / 2);
          
          // Pequeno delay para garantir que o canvas foi renderizado no novo tamanho
          setTimeout(() => {
            createWheel(stopAngle);
          }, 50);
        }
      }
      
      // Atualizar o wheelSize anterior
      previousWheelSizeRef.current = wheelSize;
    }
  }, [wheelSize, currentStep, wheelData, spinResult, isSpinning, createWheel]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isSimplified = wheelData?.wheel.simplified_form || false;

    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!isSimplified && !formData.whatsapp?.trim()) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    // Validar WhatsApp (apenas no modo completo)
    if (!isSimplified && formData.whatsapp) {
      if (!validateWhatsApp(formData.whatsapp)) {
        return;
      }
    }

    // Validar data de nascimento (apenas no modo completo)
    if (!isSimplified) {
      if (!validateBirthdate(formData.birthdate || '')) {
        return;
      }
    }

    if (!acceptedTerms) {
      toast.error('Você deve aceitar os termos para continuar');
      return;
    }

    if (!validateCustomFields(wheelData?.wheel.custom_fields || [], customFieldsData)) {
      toast.error('Por favor, preencha todos os campos obrigatórios corretamente');
      return;
    }

    const normalizedWhatsApp = !isSimplified && formData.whatsapp 
      ? normalizeWhatsApp(formData.whatsapp) 
      : '';

    // Verificar limite de giros (apenas no modo completo com WhatsApp)
    if (!isSimplified && normalizedWhatsApp) {
      try {
        setIsFormSubmitting(true);
        const checkResult = await publicApi.checkWhatsApp(companySlug!, slug!, normalizedWhatsApp);

        setIsFormSubmitting(false);
        if (!checkResult.can_spin) {
          toast.error(
            checkResult.has_limit
              ? `Você já atingiu o limite de ${checkResult.spins_limit} giro(s) nesta roda`
              : 'Você não pode girar mais vezes nesta roda'
          );
          return;
        }

        if (checkResult.has_limit && checkResult.spins_count > 0) {
          toast(`Você já girou ${checkResult.spins_count} de ${checkResult.spins_limit} vez(es)`, {
            icon: 'ℹ️',
          });
        }
      } catch (error: any) {
        setIsFormSubmitting(false);
        toast.error(error.error || 'Erro ao verificar limite de giros');
        return;
      } 
    }

    try {
      setIsFormSubmitting(true);
      const spinData: SpinRequest = {
        name: formData.name,
        whatsapp: normalizedWhatsApp || undefined,
        birthdate: !isSimplified ? formData.birthdate : undefined,
        customData: customFieldsData
      };

      const result = await publicApi.spin(companySlug!, slug!, spinData);
      setSpinResult(result);

      // Check if instant draw or scheduled draw
      const isInstantDraw = result.instant_draw !== false; // default true

      if (!isInstantDraw) {
        // Scheduled draw: just registered participation, go directly to result screen
        setCurrentStep('result');
      } else if (result.prize) {
        // Instant draw: prize already determined, show wheel with result
        const prizeIndex = wheelData!.products.findIndex(p => p.id === result.prize!.id);
        const segmentAngle = 360 / wheelData!.products.length;
        const stopAngle = (prizeIndex * segmentAngle) + (segmentAngle / 2);

        setCurrentStep('wheel');

        // Create wheel but DON'T start automatically
        setTimeout(() => {
          createWheel(stopAngle);
          previousWheelSizeRef.current = wheelSize;
        }, 100);
      }

    } catch (error: any) {
      toast.error(error.error || 'Erro ao realizar o sorteio');
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleSpin = async () => {
    if (!wheelRef.current || isSpinning) return;

    // For instant draws, just animate the wheel (prize already determined)
    setIsSpinning(true);
    wheelRef.current.startAnimation();
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldsData({
      ...customFieldsData,
      [fieldId]: value
    });
    if (customFieldsErrors[fieldId]) {
      setCustomFieldsErrors({
        ...customFieldsErrors,
        [fieldId]: ''
      });
    }
  };

  // Estados de loading e erro
  if (isLoading) {
    return <LoadingState theme={theme} />;
  }

  if (!wheelData) {
    return <ErrorState />;
  }

  // Check if scheduled draw was already executed
  if (wheelData.wheel.instant_draw === false && wheelData.wheel.draw_executed) {
    return <DrawExecutedState theme={theme} />;
  }

  if (!wheelData.stats.has_available_prizes) {
    return <NoPrizesState />;
  }

  // Renderizar etapa de pré-requisito
  if (currentStep === 'prerequisite' && wheelData.wheel.prerequisite_content) {
    return (
      <PrerequisiteModal
        isOpen={true}
        onClose={() => {
          window.close();
        }}
        onConfirm={() => {
          setPrerequisiteConfirmed(true);
          setCurrentStep('form');
        }}
        prerequisiteContent={wheelData.wheel.prerequisite_content}
        wheelName={wheelData.wheel.name}
      />
    );
  }

  // Renderizar etapa atual
  if (currentStep === 'form') {
    return (
      <SpinForm
        wheelData={wheelData}
        formData={formData}
        customFieldsData={customFieldsData}
        customFieldsErrors={customFieldsErrors}
        acceptedTerms={acceptedTerms}
        theme={theme}
        isFormSubmitting={isFormSubmitting}
        onFormChange={handleFormChange}
        onCustomFieldChange={handleCustomFieldChange}
        onTermsChange={setAcceptedTerms}
        onSubmit={handleFormSubmit}
      />
    );
  }

  if (currentStep === 'wheel') {
    return (
      <WheelCanvas
        canvasRef={canvasRef}
        wheelSize={wheelSize}
        isSpinning={isSpinning}
        formName={formData.name}
        wheelData={wheelData}
        theme={theme}
        onSpin={handleSpin}
      />
    );
  }

  if (currentStep === 'result' && spinResult) {
    return (
      <ResultScreen
        spinResult={spinResult}
        wheelData={wheelData}
        formData={formData}
        customFieldsData={customFieldsData}
      />
    );
  }

  return null;
};

export default WheelPage;
